"use client";

import * as React from "react";
import {
  useAutoplayBlocked,
  useHeroVideoVariant,
  useRevealAtVideoTime,
  type VideoVariant,
} from "./use-hero-video";

const VIDEO_BASE = "https://cdn.inito.com/inito_website/home/assets/hero/video";

// Posters are each video's first frame, so the video takes over seamlessly. End frames
// are its last frame (monitor lit), shown instead when the video can't play.
const HERO_VIDEO: Record<
  VideoVariant,
  { poster: string; endFrame: string; webm: string; mp4: string }
> = {
  mobile: {
    poster: `${VIDEO_BASE}/hero_mobile_v2_poster.jpg`,
    endFrame: `${VIDEO_BASE}/hero_mobile_v2_end.jpg`,
    webm: `${VIDEO_BASE}/hero_mobile_v2.webm`,
    mp4: `${VIDEO_BASE}/hero_mobile_v2.mp4`,
  },
  desktop: {
    poster: `${VIDEO_BASE}/hero_desktop_poster.jpg`,
    endFrame: `${VIDEO_BASE}/hero_desktop_end.jpg`,
    webm: `${VIDEO_BASE}/hero_desktop.webm`,
    mp4: `${VIDEO_BASE}/hero_desktop.mp4`,
  },
};

/**
 * The monitor + strip transition: plays once (muted, inline) and holds on its last
 * frame. Calls onReveal when it's time for the card and offer pill to animate in.
 */
export function HeroBackgroundVideo({ onReveal }: { onReveal: () => void }) {
  const variant = useHeroVideoVariant();
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [hasEnded, setHasEnded] = React.useState(false);
  const [isEndFrameLoaded, setIsEndFrameLoaded] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  // Starts playback and reports whether the browser refused it.
  const isBlocked = useAutoplayBlocked(videoRef, variant);

  // The video is in the server HTML and may start (or, on slow JS, even finish) before
  // hydration, so its playing/ended events can be missed: read its state once mounted.
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.ended) setHasEnded(true);
    else if (!video.paused && video.currentTime > 0) setIsPlaying(true);
  }, []);

  useRevealAtVideoTime(videoRef, variant, onReveal);

  // Without a playing video (autoplay refused, e.g. iOS Low Power Mode, or no video for
  // reduced motion / data saver) the first frame is a dark, unlit monitor: show the last
  // frame instead. Also once the video has ended: iOS Safari drops an ended video's frame
  // after the page sits off screen for a while, which would bare the dark poster below.
  const showEndFrame = variant === "none" || isBlocked || hasEnded;
  // Loaded (hidden) while the video plays, so it is ready when the video ends and the swap
  // to the identical still is invisible.
  const mountEndFrame = showEndFrame || isPlaying;

  // Card and offer pill animate in as soon as the end frame is on screen.
  React.useEffect(() => {
    if (showEndFrame && isEndFrameLoaded) onReveal();
  }, [showEndFrame, isEndFrameLoaded, onReveal]);

  // Sized to the hero stage's width (cqw, not the section height) so the monitor keeps
  // the Figma framing on every screen width. Mobile is enlarged and pulled up; desktop
  // spans the full width (the video is designed as a 1440px-wide background). Where the
  // stage is narrower than the screen, the video is clipped to it and its sides fade
  // into the section's matching side fill. On desktop its bottom edge fades out just
  // below the 55.7cqw hero height, so a hero stretched to the screen height runs into
  // the dark background without a seam (invisible when the hero isn't stretched).
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden min-[601px]:max-xl:[mask-image:linear-gradient(to_right,transparent,#000_8%,#000_92%,transparent)] min-[1921px]:[mask-image:linear-gradient(to_right,transparent,#000_6%,#000_94%,transparent)]"
    >
      <div className="absolute left-[-7.9cqw] top-[-21.9cqw] aspect-[860/1836] w-[114.4cqw] xl:left-0 xl:top-[-5.1cqw] xl:aspect-[1440/900] xl:w-full xl:[mask-image:linear-gradient(to_bottom,#000_97.3%,transparent)]">
        {/* <picture> so only the poster for the current layout is downloaded. Reduced-motion
            users get the end frame from the HTML, with no wait for hydration. */}
        <picture>
          <source
            media="(prefers-reduced-motion: reduce) and (min-width: 1280px)"
            srcSet={HERO_VIDEO.desktop.endFrame}
          />
          <source
            media="(prefers-reduced-motion: reduce)"
            srcSet={HERO_VIDEO.mobile.endFrame}
          />
          <source media="(min-width: 1280px)" srcSet={HERO_VIDEO.desktop.poster} />
          {/* eslint-disable-next-line @next/next/no-img-element -- art-directed poster under the video */}
          <img
            src={HERO_VIDEO.mobile.poster}
            alt=""
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </picture>
        {/* Rendered on the server so the browser fetches and autoplays it while parsing the
            HTML instead of after hydration. <source media> picks the layout (checked once,
            at load). Always visible: until its first frame decodes it is transparent over
            the identical poster, so the handover can't be seen. */}
        <video
          ref={videoRef}
          id="hero-video-player"
          data-testid="hero-video-player"
          muted
          playsInline
          autoPlay
          preload="auto"
          disablePictureInPicture
          disableRemotePlayback
          onPlaying={() => setIsPlaying(true)}
          onEnded={() => setHasEnded(true)}
          className="absolute inset-0 h-full w-full object-cover motion-reduce:hidden"
        >
          <source
            media="(min-width: 1280px)"
            src={HERO_VIDEO.desktop.webm}
            type="video/webm"
          />
          <source
            media="(min-width: 1280px)"
            src={HERO_VIDEO.desktop.mp4}
            type="video/mp4"
          />
          <source src={HERO_VIDEO.mobile.webm} type="video/webm" />
          <source src={HERO_VIDEO.mobile.mp4} type="video/mp4" />
        </video>
        {/* Not mounted before the video plays or fails, so it doesn't compete with the
            poster. Above the video, which may sit paused on its dark first frame; shown
            only once fully loaded so it appears in one go. */}
        {mountEndFrame ? (
          <picture>
            <source media="(min-width: 1280px)" srcSet={HERO_VIDEO.desktop.endFrame} />
            {/* eslint-disable-next-line @next/next/no-img-element -- must match the video's last frame exactly */}
            <img
              src={HERO_VIDEO.mobile.endFrame}
              alt=""
              decoding="async"
              onLoad={() => setIsEndFrameLoaded(true)}
              className={`absolute inset-0 h-full w-full object-cover ${showEndFrame && isEndFrameLoaded ? "opacity-100" : "opacity-0"}`}
            />
          </picture>
        ) : null}
      </div>
    </div>
  );
}
